'use client';
import React, { ReactNode, useEffect, useRef } from 'react';
import { getAnalytics, setAnalyticsConfig } from './analytics';
import { CreateAnalyticsInstanceParams } from './types';
import { useAnalytics } from './useAnalytics';

interface ManagedAnalyticsProviderProps extends CreateAnalyticsInstanceParams {
  children: ReactNode;
  /** Send one `page` event as soon as tracking is enabled (on load or on consent). */
  sendPageView?: boolean;
}

const buildSuperProperties = (
  analyticsConfig: CreateAnalyticsInstanceParams,
) => ({
  ...analyticsConfig.superProperties,
  app_name: analyticsConfig.appName,
  environment: analyticsConfig.environment,
});

/**
 * Managed Analytics Provider component that wraps children with a pre-configured analytics context.
 * This ensures consistent analytics configuration with Segment
 */
export const ManagedAnalyticsProvider: React.FC<
  ManagedAnalyticsProviderProps
> = ({ children, sendPageView = false, ...analyticsConfig }) => {
  const initialized = useRef<boolean | null>(null);
  const pageViewSent = useRef(false);
  const { trackPageView } = useAnalytics();

  if (initialized.current === null) {
    setAnalyticsConfig({
      ...analyticsConfig,
      superProperties: buildSuperProperties(analyticsConfig),
    });
    initialized.current = true;
  }

  const { enabled, superProperties, appName, environment } = analyticsConfig;

  // Re-sync when consent or superProperties change
  useEffect(() => {
    setAnalyticsConfig({
      enabled,
      superProperties: buildSuperProperties({
        superProperties,
        appName,
        environment,
      } as CreateAnalyticsInstanceParams),
    });
  }, [enabled, superProperties, appName, environment]);

  // Runs after the re-sync above, so Segment is already enabled
  useEffect(() => {
    if (!sendPageView || !enabled || pageViewSent.current) return;
    pageViewSent.current = true;
    void trackPageView();
  }, [sendPageView, enabled, trackPageView]);

  return children;
};

/**
 * Higher-order component that wraps a component with the ManagedAnalyticsProvider.
 * Usage: const WrappedComponent = withAnalytics(MyComponent, config);
 */
export const withAnalytics = <P extends object>(
  Component: React.ComponentType<P>,
  analyticsConfig: CreateAnalyticsInstanceParams,
) => {
  const WrappedComponent = (props: P) => <Component {...props} />;
  setAnalyticsConfig({
    ...analyticsConfig,
    superProperties: buildSuperProperties(analyticsConfig),
  });
  // Pre-initialize on module load for faster first call
  void getAnalytics();

  WrappedComponent.displayName = `withAnalytics(${Component.displayName || Component.name})`;

  return WrappedComponent;
};
